import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import { firebaseApp } from "../db/Firebase";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import UserContext from "./UserContext";
import "./SignUp.css";
import { FaEyeSlash, FaEye } from "react-icons/fa6";

import toast, { Toaster } from "react-hot-toast";

// Constants
const PHONE_REGEX = /^[0-9]{10}$/;
const PASSWORD_STRENGTH = {
  WEAK: { isValid: false, message: "Password is weak", class: "weak" },
  MEDIUM: {
    isValid: false,
    message: "Password strength: Medium",
    class: "medium",
  },
  STRONG: {
    isValid: true,
    message: "Password strength: Strong",
    class: "strong",
  },
};
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

function SignUp({ onClose, open, onSignUpSuccess }) {
  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });

  // Other state
  const [activeInput, setActiveInput] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [signupMethod, setSignupMethod] = useState("email");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    isValid: false,
    message: "",
  });

  // Context and navigation
  const { setUser } = useContext(UserContext);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {
    user,
    authError,
    clearAuthError,
    signUpWithEmail,
    signInWithGoogle,
    createRecaptchaVerifier,
    sendPhoneVerificationCode,
    isRateLimited,
  } = useContext(UserContext);
  // Rate limiting
  const [loginAttempts, setLoginAttempts] = useState(0);

  // Refs
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifier = useRef(null);

  useEffect(() => {
    clearAuthError();
    setShowVerificationInput(false);
  }, [signupMethod, clearAuthError]);

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifier.current) {
        try {
          recaptchaVerifier.current.clear();
          recaptchaVerifier.current = null;
        } catch (error) {
          console.error("Error clearing recaptcha:", error);
        }
      }
    };
  }, []);

  // Navigate if already logged in
  useEffect(() => {
    if (user) {
      navigate("/Home");
    }
  }, [user, navigate]);

  useEffect(() => {
    const createBubble = () => {
      const bubbleContainer = document.querySelector(".background-animation");
      if (!bubbleContainer) return;

      const bubble = document.createElement("div");
      bubble.classList.add("bubble");

      // Random size between 10px and 50px
      const size = Math.random() * 40 + 10;
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;

      // Random position
      bubble.style.left = `${Math.random() * 100}%`;

      // Random opacity
      bubble.style.opacity = `${Math.random() * 0.3 + 0.1}`;

      // Random animation duration
      const duration = Math.random() * 10 + 10;
      bubble.style.animationDuration = `${duration}s`;

      bubbleContainer.appendChild(bubble);

      // Remove bubble after animation completes
      setTimeout(() => {
        bubble.remove();
      }, duration * 1000);
    };

    // Create bubbles at intervals
    const interval = setInterval(createBubble, 1000);

    // Initial bubbles
    for (let i = 0; i < 10; i++) {
      createBubble();
    }

    return () => clearInterval(interval);
  }, []);

  const sanitizeInput = (input) => {
    if (!input) return "";

    // Basic XSS prevention - remove script tags and other dangerous HTML
    let sanitized = input
      .toString()
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/on\w+="[^"]*"/g, "")
      .replace(/javascript:/gi, "")
      .trim();

    return sanitized;
  };

  const validatePassword = (password) => {
    if (!password) {
      setPasswordStrength({
        isValid: false,
        message: "",
        class: "",
      });
      return;
    }

    // Simplified scoring system with better feedback
    let score = 0;
    let className = "";

    // Basic checks
    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Real-time specific feedback based on what's missing
    let feedbackMessage = "";
    let isValid = false;

    if (score <= 2) {
      feedbackMessage = "Add uppercase, numbers, and special characters";
      isValid = false;
      className = "weak";
    } else if (score <= 3) {
      feedbackMessage =
        "Almost there! Add " +
        (!/[A-Z]/.test(password) ? "uppercase, " : "") +
        (!/[0-9]/.test(password) ? "numbers, " : "") +
        (!/[^A-Za-z0-9]/.test(password) ? "special characters" : "");
      feedbackMessage = feedbackMessage.replace(/, $/, "");
      isValid = false;
      className = "medium";
    } else {
      feedbackMessage = "Password strength: Strong";
      isValid = true;
      className = "strong";
    }

    setPasswordStrength({
      isValid: isValid,
      message: feedbackMessage,
      class: className,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    setFormData({
      ...formData,
      [name]: sanitizedValue,
    });

    // Call validatePassword directly when password field changes
    if (name === "password") {
      validatePassword(sanitizedValue);
    }
  };
  useEffect(() => {
    // Initialize RecaptchaVerifier for phone authentication
    if (
      signupMethod === "phone" &&
      !recaptchaVerifier.current &&
      recaptchaContainerRef.current
    ) {
      try {
        // Clear any existing recaptcha instance
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        }

        recaptchaVerifier.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerRef.current,
          {
            size: "invisible",
            callback: () => {
              // reCAPTCHA solved - no need for console log in production
            },
            "expired-callback": () => {
              setError("Security verification expired. Please try again.");
            },
          }
        );
      } catch (error) {
        setError(
          "Failed to initialize verification. Please refresh and try again."
        );
      }
    }

    // Cleanup function
    return () => {
      if (recaptchaVerifier.current) {
        try {
          recaptchaVerifier.current.clear();
          recaptchaVerifier.current = null;
        } catch (error) {
          // Silent cleanup error
        }
      }
    };
  }, [signupMethod]);

  // Phone verification
  const startPhoneVerification = async () => {
    // Form validation
    if (!formData.phoneNumber || !formData.firstName || !formData.lastName) {
      toast.error("Please enter your name and phone number");
      return;
    }

    // Phone number format validation
    if (!PHONE_REGEX.test(formData.phoneNumber)) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    try {
      // Format phone number if needed
      let phoneNumber = formData.phoneNumber;
      if (!phoneNumber.startsWith("+")) {
        phoneNumber = "+91" + phoneNumber; // Default to India code
      }

      // Initialize recaptcha if needed
      if (!recaptchaVerifier.current) {
        recaptchaVerifier.current = createRecaptchaVerifier(
          "recaptcha-container"
        );

        if (!recaptchaVerifier.current) {
          throw new Error("Failed to initialize verification");
        }
      }

      // Send verification code
      const confirmation = await sendPhoneVerificationCode(
        phoneNumber,
        recaptchaVerifier.current
      );

      setConfirmationResult(confirmation);
      setShowVerificationInput(true);
      toast.success("Verification code sent!");

      // Set code expiration timer (5 minutes)
      setTimeout(() => {
        if (showVerificationInput) {
          setShowVerificationInput(false);
          toast.error("Verification code expired. Please request a new one.");
        }
      }, 5 * 60 * 1000);
    } catch (error) {
      toast.error(error.message || "Failed to send verification code");

      // Reset recaptcha on error
      if (recaptchaVerifier.current) {
        try {
          recaptchaVerifier.current.clear();
          recaptchaVerifier.current = null;
        } catch (error) {
          console.error("Error clearing recaptcha:", error);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify phone code
  const verifyPhoneCode = async () => {
    if (!verificationCode) {
      toast.error("Please enter verification code");
      return;
    }

    setLoading(true);
    try {
      await confirmationResult.confirm(verificationCode);
      toast.success("Phone number verified successfully!");
      resetForm();

      if (onSignUpSuccess) {
        onSignUpSuccess();
      } else {
        navigate("/Home");
      }
    } catch (error) {
      toast.error("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const verifyPhoneNumber = async () => {
    if (!verificationCode) {
      toast.error("Please enter verification code");
      return;
    }

    setLoading(true);
    try {
      const result = await confirmationResult.confirm(verificationCode);
      const user = result.user;

      // Create user document in Firestore with sanitized data
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        phoneNumber: user.phoneNumber,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        createdAt: serverTimestamp(),
        role: "customer", // Default role for security
        lastLogin: serverTimestamp(),
      });

      // Update context
      setUser(user);

      toast.success("Phone number verified successfully!");
      resetForm();

      if (onSignUpSuccess) {
        onSignUpSuccess();
      } else {
        navigate("/Home");
      }
    } catch (error) {
      const MAX_ATTEMPTS = 5;
      if (MAX_ATTEMPTS) {
        // isRateLimited is a state value, not a function
        setLoginAttempts(MAX_ATTEMPTS);
        setError("Too many failed attempts. Please try again later.");

        // Set a timeout to reset after delay
        setTimeout(() => {
          setLoginAttempts(0);
          setError(null);
        }, 10 * 60 * 1000); // 10 minutes
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Email signup
  const signup = async (e) => {
    e?.preventDefault();

    // Form validation
    if (
      !formData.email ||
      !formData.password ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.phoneNumber
    ) {
      toast.error("Please fill all fields");
      return;
    }

    // Password strength validation
    if (!passwordStrength.isValid) {
      toast.error("Please use a stronger password");
      return;
    }

    // Phone format validation - much more flexible now
    if (!/^\+?[0-9]{10,15}$/.test(formData.phoneNumber)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);

      // Format phone number if needed
      let phoneNumber = formData.phoneNumber;
      if (!phoneNumber.startsWith("+")) {
        phoneNumber = "+91" + phoneNumber; // Default to India code for backend
      }

      await signUpWithEmail(formData.email, formData.password, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: phoneNumber, // Use the formatted number
      });

      toast.success("Signup Successful");
      resetForm();

      if (onSignUpSuccess) {
        onSignUpSuccess();
      } else {
        navigate("/SignIn");
      }
    } catch (error) {
      toast.error(error.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };
  const handleInputFocus = (name) => {
    setActiveInput(name);
  };

  const handleInputBlur = () => {
    setActiveInput(null);
  };
  // Google signup
  const signUpWithGoogle = async () => {
    setLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithGoogle();

      toast.success("Sign up with Google successful!");
      resetForm();

      if (onSignUpSuccess) {
        onSignUpSuccess();
      } else {
        navigate("/Home");
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      toast.error(error.message || "Google sign up failed");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
    });
    setVerificationCode("");
    setShowVerificationInput(false);
    setPasswordStrength({ isValid: false, message: "" });
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      signup();
    }
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="signup-element">
        <div className="background-animation"></div>
        <div className="signup-content">
          <motion.div
            className={`modal ${open ? "open" : ""}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="signup-container">
              <div className="signup-form-wrapper">
                <motion.div
                  className="signup-header"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <h1 className="signup-title">Sign Up</h1>
                  {authError && (
                    <motion.div
                      className="error-message"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                    >
                      <p>{authError}</p>
                    </motion.div>
                  )}
                </motion.div>

                <div className="signup-method-toggle">
                  <motion.div
                    className="toggle-slider"
                    initial={false}
                    animate={{
                      x: signupMethod === "email" ? 0 : "100%",
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  ></motion.div>
                  <button
                    onClick={() => setSignupMethod("email")}
                    className={signupMethod === "email" ? "active" : ""}
                  >
                    Email
                  </button>
                  <button
                    onClick={() => setSignupMethod("phone")}
                    className={signupMethod === "phone" ? "active" : ""}
                  >
                    Phone
                  </button>
                </div>

                <div className="form-fields">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="form-fields"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.div
                        whileTap={{ scale: 0.98 }}
                        className={`input-field ${
                          activeInput === "firstName" ? "active" : ""
                        }`}
                      >
                        <input
                          type="text"
                          name="firstName"
                          id="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="input"
                          disabled={loading || isRateLimited}
                          onFocus={() => handleInputFocus("firstName")}
                          onBlur={handleInputBlur}
                        />
                        <label
                          htmlFor="firstName"
                          className={formData.firstName ? "filled" : ""}
                        >
                          First Name
                        </label>
                      </motion.div>

                      <motion.div
                        whileTap={{ scale: 0.98 }}
                        className={`input-field ${
                          activeInput === "lastName" ? "active" : ""
                        }`}
                      >
                        <input
                          type="text"
                          name="lastName"
                          id="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="input"
                          disabled={loading || isRateLimited}
                          onFocus={() => handleInputFocus("lastName")}
                          onBlur={handleInputBlur}
                        />
                        <label
                          htmlFor="lastName"
                          className={formData.lastName ? "filled" : ""}
                        >
                          Last Name
                        </label>
                      </motion.div>

                      <motion.div
                        whileTap={{ scale: 0.98 }}
                        className={`input-field ${
                          activeInput === "phoneNumber" ? "active" : ""
                        }`}
                      >
                        <input
                          type="tel"
                          name="phoneNumber"
                          id="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          className="input"
                          disabled={loading || isRateLimited}
                          onFocus={() => handleInputFocus("phoneNumber")}
                          onBlur={handleInputBlur}
                        />
                        <label
                          htmlFor="phoneNumber"
                          className={formData.phoneNumber ? "filled" : ""}
                        >
                          Phone Number
                        </label>
                      </motion.div>

                      {signupMethod === "email" ? (
                        <motion.form
                          onKeyDown={handleKeyPress}
                          className="signup-form"
                          key="email-form"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                        >
                          <motion.div
                            whileTap={{ scale: 0.98 }}
                            className={`input-field ${
                              activeInput === "email" ? "active" : ""
                            }`}
                          >
                            <input
                              type="email"
                              name="email"
                              id="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="input"
                              disabled={loading || isRateLimited}
                              onFocus={() => handleInputFocus("email")}
                              onBlur={handleInputBlur}
                            />
                            <label
                              htmlFor="email"
                              className={formData.email ? "filled" : ""}
                            >
                              Email
                            </label>
                          </motion.div>

                          <motion.div
                            whileTap={{ scale: 0.98 }}
                            className={`input-field ${
                              activeInput === "password" ? "active" : ""
                            }`}
                          >
                            <div className="password-input-container">
                              <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                id="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className="input"
                                disabled={loading || isRateLimited}
                                onFocus={() => handleInputFocus("password")}
                                onBlur={handleInputBlur}
                              />
                              <label
                                htmlFor="password"
                                className={formData.password ? "filled" : ""}
                              >
                                Password
                              </label>
                              <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <FaEye /> : <FaEyeSlash />}
                              </button>
                            </div>
                            {formData.password && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className={`password-strength ${passwordStrength.class}`}
                              >
                                {passwordStrength.message}
                              </motion.div>
                            )}
                          </motion.div>

                          <div className="button-field">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={signup}
                              className="btn primary-btn"
                              disabled={loading || isRateLimited}
                            >
                              {loading ? "Signing Up..." : "Sign Up with Email"}
                            </motion.button>
                          </div>
                        </motion.form>
                      ) : (
                        <motion.div
                          key="phone-form"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                        >
                          {!showVerificationInput ? (
                            <div className="button-field">
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                id="phone-sign-in-button"
                                onClick={startPhoneVerification}
                                className="btn primary-btn"
                                disabled={loading || isRateLimited}
                              >
                                {loading
                                  ? "Sending Code..."
                                  : "Send Verification Code"}
                              </motion.button>
                            </div>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              className="verification-inputs"
                            >
                              <motion.div
                                whileTap={{ scale: 0.98 }}
                                className={`input-field ${
                                  activeInput === "verificationCode"
                                    ? "active"
                                    : ""
                                }`}
                              >
                                <input
                                  type="text"
                                  value={verificationCode}
                                  onChange={(e) =>
                                    setVerificationCode(e.target.value)
                                  }
                                  className="input"
                                  onFocus={() =>
                                    handleInputFocus("verificationCode")
                                  }
                                  onBlur={handleInputBlur}
                                />
                                <label
                                  htmlFor="verificationCode"
                                  className={verificationCode ? "filled" : ""}
                                >
                                  Verification Code
                                </label>
                              </motion.div>
                              <div className="button-field">
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={verifyPhoneNumber}
                                  className="btn primary-btn"
                                  disabled={loading || isRateLimited}
                                >
                                  {loading ? "Verifying..." : "Verify Code"}
                                </motion.button>
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  <div
                    ref={recaptchaContainerRef}
                    id="recaptcha-container"
                  ></div>

                  <div className="button-field">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={signUpWithGoogle}
                      className="btn google-btn"
                      disabled={loading || isRateLimited}
                    >
                      {loading && signupMethod === "google"
                        ? "Loading..."
                        : "Sign Up with Google"}
                    </motion.button>
                  </div>

                  <div className="login-link">
                    <p>
                      Already have an account?{" "}
                      <Link to={"/SignIn"}>Sign In</Link>
                    </p>
                  </div>

                  {isRateLimited && (
                    <motion.p
                      className="rate-limit-message"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                    >
                      Too many requests. Please wait before trying again.
                    </motion.p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default SignUp;
